import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider, AuthCallback, ProtectedRoute } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { MemberProvider } from "@/lib/memberAuth";
import { CartDrawer } from "@/components/CartDrawer";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Leadership from "@/pages/Leadership";
import Ministries from "@/pages/Ministries";
import Events from "@/pages/Events";
import Sermons from "@/pages/Sermons";
import Give from "@/pages/Give";
import GiveSuccess from "@/pages/GiveSuccess";
import GivePaypalReturn from "@/pages/GivePaypalReturn";
import Contact from "@/pages/Contact";
import PlanVisit from "@/pages/PlanVisit";
import Store from "@/pages/Store";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import MemberAuth from "@/pages/MemberAuth";
import MemberOAuthCallback from "@/pages/MemberOAuthCallback";
import Account from "@/pages/Account";
import CardVerify from "@/pages/CardVerify";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import CheckIn from "@/pages/CheckIn";

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname, hash]);
  return null;
};

function Layout() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin") || pathname.startsWith("/checkin");
  return (
    <>
      {!isAdmin && <Navbar />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/leadership" element={<Leadership />} />
          <Route path="/ministries" element={<Ministries />} />
          <Route path="/events" element={<Events />} />
          <Route path="/sermons" element={<Sermons />} />
          <Route path="/give" element={<Give />} />
          <Route path="/give/success" element={<GiveSuccess />} />
          <Route path="/give/paypal/return" element={<GivePaypalReturn />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/plan-your-visit" element={<PlanVisit />} />
          <Route path="/store" element={<Store />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order/success" element={<OrderSuccess />} />
          <Route path="/account/login" element={<MemberAuth />} />
          <Route path="/account/oauth" element={<MemberOAuthCallback />} />
          <Route path="/account" element={<Account />} />
          <Route path="/card/:code" element={<CardVerify />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/checkin/:code" element={<CheckIn />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    // Member Google OAuth returns to /account/oauth; admin OAuth uses its own callback
    if (location.pathname.startsWith("/account")) return <MemberOAuthCallback />;
    return <AuthCallback />;
  }
  return <Layout />;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <MemberProvider>
            <CartProvider>
              <ScrollManager />
              <AppRouter />
              <CartDrawer />
              <Toaster position="top-center" richColors />
            </CartProvider>
          </MemberProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
